import React, {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {isEqual} from "lodash";
import {
  ChoiceUserInputAction,
  ChoiceUserInputBranch,
  ChoiceUserInputBranchState,
  ChallengeInputMode,
  ChallengeMode,
} from "../../../../schema";

import Content from "./Content";
import Phrase, {PhraseVariant, getTextWithStressMarks} from "../../../Phrase";

import {useDispatch, useSelector} from "react-redux";
import {getChallenge} from "../../../../redux/player/selectors";
import {PlayerReduxState, UserInputDisplayMode} from "../../../../redux/player/types";
import {ChoiceUserInputDisplayMode} from "../../../../redux/player/selectors/getCurrentActionChoiceMode";

import classes from "./ChoiceUserInputActionContent.module.css";
import selectInput from "../../../../redux/player/actions/selectInput";
import Checkbox from "../../../../ui/checkbox";
import UserInputActionContent from "./UserInputActionContent";

type ChoiceUserInputActionContentProps = {
  action: ChoiceUserInputAction;
  phraseMode: PhraseVariant | undefined;
  choiceMode: ChoiceUserInputDisplayMode | undefined;
  displayMode?: UserInputDisplayMode | undefined;
  chosenBranches: Set<string>;
  onlyShowChosenBranches?: boolean;
  isHistory?: boolean;
  feedbackText?: string;
};

function ChoiceUserInputActionContent({
  action,
  phraseMode,
  choiceMode,
  displayMode,
  chosenBranches,
  onlyShowChosenBranches = false,
  isHistory = false,
  feedbackText,
}: ChoiceUserInputActionContentProps) {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const challenge = useSelector(ChoiceUserInputActionContent.selector, isEqual);
  const isTextInput = challenge.inputMode === ChallengeInputMode.TEXT;
  const isInputSelectable =
    action.isMultipleChoice ||
    (challenge.mode === ChallengeMode.CHOICES_WITH_CUSTOM_SCORES && challenge.selectableInputs) ||
    (challenge.mode === ChallengeMode.CUSTOM_PARAMETERS_TEST && challenge.selectableInputs) ||
    (challenge.mode === ChallengeMode.CHOICE_BASED_STUDY && challenge.selectableInputs);
  const [isClickRegistered, registerClick] = useState(false);

  const branches: ChoiceUserInputBranch[] = useMemo(() => {
    let list = action.branches;
    if (onlyShowChosenBranches && chosenBranches.size > 0) {
      list = list.filter((b) => chosenBranches.has(b.id));
    } else {
      switch (choiceMode) {
        case ChoiceUserInputDisplayMode.ALL:
        case ChoiceUserInputDisplayMode.ALL_WITH_HINT:
          list = action.branches;
          break;
        case ChoiceUserInputDisplayMode.ONLY_VALID:
          list = action.branches.filter((b) => b.state === ChoiceUserInputBranchState.CORRECT);
          break;
        default:
          console.error("NotImplemented");
          list = [];
      }
    }
    return list;
  }, [action.branches, choiceMode, onlyShowChosenBranches, chosenBranches]);

  const selectInputHandler = useCallback(
    (branchIndex: number) => {
      if (isInputSelectable && !isClickRegistered) {
        registerClick(true);
        dispatch(selectInput(branchIndex));

        if (
          action.isMultipleChoice ||
          (challenge.mode === ChallengeMode.CHOICE_BASED_STUDY && challenge.selectableInputs)
        ) {
          registerClick(false);
        }
      }
    },
    [
      dispatch,
      isInputSelectable,
      isClickRegistered,
      challenge.mode,
      challenge.selectableInputs,
      action.isMultipleChoice,
    ],
  );

  const onContentClick = useCallback(
    (e: React.MouseEvent) => {
      const branchIndex = parseInt(e.currentTarget.getAttribute("data-index")!);
      selectInputHandler(branchIndex);
    },
    [selectInputHandler],
  );

  const help = useMemo(() => {
    if (isTextInput) {
      if (isInputSelectable) {
        return (
          <>
            {t("player.choiceUserInputAction.text.helpTextAlt")}
            <br />
            {t("player.choiceUserInputAction.text.multipleChoice")}
          </>
        );
      } else {
        return t("player.choiceUserInputAction.text.helpText");
      }
    } else {
      if (isInputSelectable) {
        return (
          <>
            {t("player.choiceUserInputAction.voice.helpTextAlt")}
            <br />
            {t("player.choiceUserInputAction.voice.multipleChoice")}
          </>
        );
      } else {
        return t("player.choiceUserInputAction.voice.helpText");
      }
    }
  }, [isTextInput, isInputSelectable, t]);

  return (
    <>
      {!isHistory && <Content variant="help">{help}</Content>}
      {!isHistory && challenge.mode === ChallengeMode.COMPETENCE_TEST && (
        <UserInputActionContent
          action={action}
          phraseMode={phraseMode}
          displayMode={UserInputDisplayMode.EXPECTED_TEXT}
        />
      )}

      {branches.map((b, index) => (
        <Content
          data-index={index}
          key={b.id}
          variant="user"
          onClick={onContentClick}
          className={
            chosenBranches.has(b.id) && b.state !== ChoiceUserInputBranchState.CORRECT && !action.isMultipleChoice
              ? classes.incorrectChoice
              : undefined
          }
        >
          <div className={classes.phraseDiv}>
            {action.isMultipleChoice && (
              <Checkbox
                variant="userChoice"
                checked={chosenBranches.has(b.id)}
                onChange={() => selectInputHandler(index)}
                onClick={(e) => e.stopPropagation()}
              />
            )}

            <Phrase
              text={b.userInput.hintText ? b.userInput.hintText : b.userInput.referenceText}
              variant={PhraseVariant.DEFAULT}
              italic={!!b.userInput.hintText}
            />
          </div>
        </Content>
      ))}

      {isHistory && feedbackText && (
        <Content.Container appear>
          <Content variant="avatar">
            {feedbackText}
          </Content>
        </Content.Container>
      )}

      {isHistory &&
        chosenBranches.size > 0 &&
        Array.from(chosenBranches).map((branchId) => {
          const branch = action.branches.find((b) => b.id === branchId);
          if (!branch) return null;
          return (
            <React.Fragment key={branchId}>
              {branch.avatarReaction?.text && (
                <Content.Container appear>
                  <Content variant="avatar">
                    <Phrase
                      text={getTextWithStressMarks(branch.avatarReaction.text)}
                      variant={PhraseVariant.DEFAULT}
                    />
                  </Content>
                </Content.Container>
              )}
              {branch.systemReaction?.text && (
                <Content.Container appear>
                  <Content variant="system">
                    <Phrase
                      text={getTextWithStressMarks(branch.systemReaction.text)}
                      variant={PhraseVariant.DEFAULT}
                    />
                  </Content>
                </Content.Container>
              )}
            </React.Fragment>
          );
        })}
    </>
  );
}

export default ChoiceUserInputActionContent;

ChoiceUserInputActionContent.selector = (state: PlayerReduxState) => {
  return getChallenge(state);
};
